// ReviewsSection.tsx
"use client";
import React, { Suspense } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@packages/ui/src/components/shadcn/button";
import { reviewsData, overallRating } from "./reviews";
import ContainerGlob from "@/app/[locale]/componentsGLob/ContainerGlob";
import { useTranslation } from "react-i18next";

const ReviewsCarousel = React.lazy(() =>
  import("./reviewsCarousel").then((module) => ({
    default: module.ReviewsCarousel,
  }))
);

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

function ReviewsLoadingSkeleton() {
  return (
    <div className="h-[400px] w-full flex items-center justify-center bg-gray-100 rounded-xl">
      <p className="text-gray-500">Caricamento recensioni...</p>
    </div>
  );
}

export function ReviewsSection() {
  const { t } = useTranslation("reviews");

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={sectionVariants}
      className="w-full bg-white py-16 md:py-24"
      aria-labelledby="reviews-section-title"
    >
      <ContainerGlob>
        <motion.h2
          variants={itemVariants}
          className="mb-12 text-center font-manrope text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl"
          id="reviews-section-title"
        >
          {t("sectionTitle")}
        </motion.h2>

        <Suspense fallback={<ReviewsLoadingSkeleton />}>
          <ReviewsCarousel reviews={reviewsData} />
        </Suspense>

        <motion.div variants={itemVariants} className="mt-16 text-center">
          <p className="mb-2 text-gray-600">{t("overallRating.label")}</p>
          <div className="mb-2 flex items-center justify-center gap-2">
            <FontAwesomeIcon
              icon={faStar}
              className="h-5 w-5 text-yellow-400"
              aria-hidden="true"
            />
            <span
              className="font-manrope text-2xl text-gray-900"
              aria-label={t("overallRating.averageAriaLabel", {
                rating: overallRating.average.toFixed(1),
              })}
            >
              {overallRating.average.toFixed(1)}
            </span>
            <span className="text-gray-500">/5</span>
          </div>
          <p className="text-gray-600">
            {t("overallRating.basedOn")}{" "}
            <span className="font-manrope text-gray-900">
              {overallRating.totalReviews} {t("overallRating.reviews")}
            </span>{" "}
            {t("overallRating.onGoogle")}
          </p>
          <Button
            variant="link"
            className="mt-4 text-blue-600 hover:text-blue-800"
            asChild
          >
            <a
              href={overallRating.googleProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("actions.viewAllReviewsAriaLabel")}
            >
              {t("actions.viewAllReviews")}
            </a>
          </Button>
        </motion.div>
      </ContainerGlob>
    </motion.section>
  );
}
